import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { CheckCircle2, ShieldCheck, Cloud, Lock, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

const FEATURES = [
  "Staff Training & Compliance",
  "Supervision & Appraisals",
  "Audits & Quality Management",
  "Medication & Incident Management",
];

/** Subtle healthcare-themed dot/line network pattern.
 *  Rendered inline so it inherits the dark navy behind — kept at 6% opacity
 *  so it never competes with the text.
 */
function HeroBackdrop() {
  return (
    <svg
      aria-hidden="true"
      className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
      viewBox="0 0 800 800"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="glow" cx="30%" cy="20%" r="55%">
          <stop offset="0%" stopColor="#22C55E" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
        </radialGradient>
        <pattern id="dotgrid" width="32" height="32" patternUnits="userSpaceOnUse">
          <circle cx="1.2" cy="1.2" r="1.2" fill="#94A3B8" />
        </pattern>
      </defs>
      <rect width="800" height="800" fill="url(#dotgrid)" />
      <rect width="800" height="800" fill="url(#glow)" />
      {/* connected-care network lines */}
      <g stroke="#93C5FD" strokeWidth="0.6" fill="none">
        <path d="M60 700 L240 520 L420 640 L620 460 L760 580" />
        <path d="M100 120 L280 280 L500 180 L680 320" />
        <path d="M40 380 L200 300 L380 420 L560 340 L740 440" />
      </g>
      <g fill="#22C55E">
        <circle cx="240" cy="520" r="3" />
        <circle cx="420" cy="640" r="3" />
        <circle cx="500" cy="180" r="3" />
        <circle cx="380" cy="420" r="3" />
        <circle cx="680" cy="320" r="3" />
      </g>
    </svg>
  );
}

function BrandLockup({ theme = "dark", size = "md" }) {
  const dims = size === "lg"
    ? { logo: "w-14 h-14", primary: "text-[26px]", sub: "text-[12px]" }
    : { logo: "w-11 h-11", primary: "text-[22px]", sub: "text-[11px]" };
  const primaryColor = theme === "dark" ? "text-slate-100" : "text-slate-900";
  const subColor = theme === "dark" ? "text-slate-400" : "text-slate-500";
  return (
    <div className="flex items-center gap-3" data-testid="brand-lockup">
      <img
        src="/brand/icon-192.png"
        alt="Ayobell"
        className={`${dims.logo} object-contain shrink-0 drop-shadow-[0_4px_18px_rgba(34,197,94,0.18)]`}
      />
      <div className="min-w-0">
        <div className={`font-heading font-bold tracking-[0.14em] leading-none ${primaryColor} ${dims.primary}`}>AYOBELL</div>
        <div className={`uppercase tracking-[0.20em] mt-1.5 ${subColor} ${dims.sub}`}>Care Management Platform</div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden">
      {/* Left — hero */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden bg-[#0F172A] text-slate-100 p-14 border-r border-slate-800">
        <HeroBackdrop />

        <div className="relative z-10 login-fade-in">
          <BrandLockup theme="dark" size="lg" />
        </div>

        {/* Hero logo — centred in the empty area between the top brand
            lockup and the "One Platform · Eight Systems" tagline. Uses the
            existing high-resolution Ayobell mark; its navy tile blends into
            the panel background so no visible box appears. */}
        <div
          className="relative z-10 flex justify-center login-fade-in login-delay-1"
          data-testid="login-hero-logo"
        >
          <img
            src="/brand/icon-512.png"
            alt=""
            aria-hidden="true"
            className="w-40 h-40 object-contain drop-shadow-[0_8px_28px_rgba(34,197,94,0.16)] select-none pointer-events-none"
          />
        </div>

        <div className="relative z-10 max-w-lg login-fade-in login-delay-1">
          <div className="text-[11px] uppercase text-emerald-400 mb-5 font-semibold" style={{ letterSpacing: "0.32em" }}>
            One Platform · Eight Systems
          </div>
          <h1 className="font-heading text-[46px] leading-[1.05] tracking-tight mb-4 text-white">
            Care Management Platform
          </h1>
          <p className="text-slate-400 text-[15px] leading-relaxed max-w-md">
            The complete digital platform for supported living, domiciliary care and residential care.
          </p>

          <ul className="mt-9 space-y-3.5" data-testid="hero-features">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 text-[15px] text-slate-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" strokeWidth={2.2} />
                <span>{f}</span>
              </li>
            ))}
            <li className="text-[13px] text-slate-500 italic pl-8">…and much more.</li>
          </ul>
        </div>

        <div className="relative z-10 text-[11px] text-slate-500 space-y-0.5 login-fade-in login-delay-2">
          <div className="text-slate-400">© 2026 Ayobell</div>
          <div className="uppercase tracking-widest">Powered by TechSpace Solutions Ltd</div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-sm login-fade-in login-delay-1">
          <div className="lg:hidden mb-8">
            <BrandLockup theme="light" size="md" />
          </div>

          <h2 className="font-heading text-[28px] font-semibold text-slate-900 leading-tight">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Access your Ayobell workspace.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                Work email
              </Label>
              <Input
                id="email" data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email"
                placeholder="you@care-provider.co.uk"
                className="mt-2 h-11 login-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                Password
              </Label>
              <Input
                id="password" data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password"
                className="mt-2 h-11 login-input"
              />
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1e293b] font-semibold tracking-wide transition-all duration-200 hover:-translate-y-[1px] hover:shadow-lg"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="text-xs text-slate-500 hover:text-slate-900 hover:underline underline-offset-2 transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          {/* Trust indicators */}
          <div className="mt-6 flex items-center justify-between gap-2" data-testid="trust-indicators">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-500 font-medium">
              <Lock className="w-3 h-3 text-emerald-600" />
              Secure Cloud
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-500 font-medium">
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              CQC Ready
            </div>
            <div className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-500 font-medium">
              <Cloud className="w-3 h-3 text-emerald-600" />
              Role-Based Security
            </div>
          </div>

          {/* Help */}
          <div
            className="mt-8 border-t border-slate-200 pt-5 flex items-start gap-2 text-xs text-slate-500"
            data-testid="login-help"
          >
            <HelpCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
            <div>
              <span className="font-medium text-slate-700">Need help signing in?</span>{" "}
              Contact your organisation administrator.
            </div>
          </div>

          {/* Mobile footer — matches left panel */}
          <div className="lg:hidden mt-8 text-center text-[10px] text-slate-400 uppercase tracking-widest">
            © 2026 Ayobell · Powered by TechSpace Solutions Ltd
          </div>
        </div>
      </div>
    </div>
  );
}
